import * as React from 'react'

import { cn } from '#/shared/lib/utils'
import { Input } from '#/shared/ui/input'
import { SearchIcon } from '#/shared/ui/icons'

type SearchInputSize = 'default' | 'sm'

interface SearchInputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
  size?: SearchInputSize
  clearable?: boolean
}

// The `focus-within:w-*` variant is intentionally omitted for the disabled
// variant — a disabled input shouldn't expand even if the browser briefly
// focuses it on click.
const sizeConfig = {
  default: {
    inputSize: 'default' as const,
    iconClass: 'size-5',
    restingWidth: 'w-56',
    focusedWidth: 'focus-within:w-72',
  },
  sm: {
    inputSize: 'sm' as const,
    iconClass: 'size-4.5',
    restingWidth: 'w-28 sm:w-40',
    focusedWidth: 'focus-within:w-40 sm:focus-within:w-56',
  },
} as const

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ size = 'default', className, clearable, ...props }, ref) => {
    const config = sizeConfig[size]

    return (
      <Input
        ref={ref}
        inputSize={config.inputSize}
        clearable={clearable}
        className={cn(
          'transition-[width] duration-300 ease-out',
          config.restingWidth,
          !props.disabled && !props.readOnly && config.focusedWidth,
          className,
        )}
        startAdornment={<SearchIcon className={config.iconClass} />}
        {...props}
      />
    )
  },
)
SearchInput.displayName = 'SearchInput'

export { SearchInput }
export type { SearchInputProps }
